//src/pages/gro/ActionPlanModelsPage.tsx
import { useMemo, useState } from "react";
import { CheckCircle2, Eye, Pencil, Plus, Search, XCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { PageHeader } from "@/components/shared/PageHeader";
import { EmptyState } from "@/components/shared/EmptyState";
import { PermissionButton } from "@/components/shared/PermissionButton";
import { PermissionIconButton } from "@/components/shared/PermissionIconButton";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { useActionPlanModels, useSetActionPlanModelStatus } from "@/hooks/useActionPlanModels";
import { PERMISSIONS } from "@/lib/permissions";
import { groReadPermission, groWritePermission, type GroCrudScope } from "@/lib/groCrudScope";

function statusBadge(isActive: boolean) {
  return isActive ? <Badge>Ativo</Badge> : <Badge variant="outline">Inativo</Badge>;
}

type ActionPlanModelsPageProps = {
  scope?: GroCrudScope;
  basePath?: string;
  title?: string;
  description?: string;
};

export default function ActionPlanModelsPage({
  scope = "client",
  basePath = "/gro/action-plan-models",
  title = "Planos de Ação",
  description = "Cadastre planos reutilizáveis e suas ações para uso posterior nas regras e no inventário.",
}: ActionPlanModelsPageProps) {
  const navigate = useNavigate();
  const readPermission = groReadPermission(scope, PERMISSIONS.ACTION_PLANS_READ);
  const writePermission = groWritePermission(scope, PERMISSIONS.ACTION_PLANS_WRITE);
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive">("all");

  const modelsQ = useActionPlanModels({
    q,
    is_active: statusFilter === "all" ? undefined : statusFilter === "active",
  }, scope);
  const setStatusMut = useSetActionPlanModelStatus(scope);

  const items = modelsQ.data ?? [];
  const counts = useMemo(
    () => ({
      total: items.length,
      active: items.filter((item) => item.is_active).length,
      inactive: items.filter((item) => !item.is_active).length,
    }),
    [items],
  );

  async function toggleStatus(id: string, next: boolean) {
    await setStatusMut.mutateAsync({ modelId: id, is_active: next });
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={title}
        description={description}
      >
        <PermissionButton
          permission={writePermission}
          onClick={() => navigate(`${basePath}/novo`)}
        >
          <Plus className="mr-2 h-4 w-4" /> Novo plano
        </PermissionButton>
      </PageHeader>

      <div className="grid gap-4 md:grid-cols-3">
        <Card className="p-4"><div className="text-sm text-muted-foreground">Total</div><div className="text-2xl font-semibold">{counts.total}</div></Card>
        <Card className="p-4"><div className="text-sm text-muted-foreground">Ativos</div><div className="text-2xl font-semibold">{counts.active}</div></Card>
        <Card className="p-4"><div className="text-sm text-muted-foreground">Inativos</div><div className="text-2xl font-semibold">{counts.inactive}</div></Card>
      </div>

      <Card className="p-4">
        <div className="grid gap-3 md:grid-cols-[1fr_220px]">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9"
              placeholder="Buscar por código, título ou descrição"
            />
          </div>
          <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as typeof statusFilter)}>
            <SelectTrigger><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="active">Ativos</SelectItem>
              <SelectItem value="inactive">Inativos</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      {items.length === 0 ? (
        <EmptyState
          title="Nenhum plano cadastrado"
          description="Cadastre o primeiro plano de ação reutilizável."
          actionLabel="Cadastrar primeiro plano"
          actionPermission={writePermission}
          onAction={() => navigate(`${basePath}/novo`)}
        />
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Código</TableHead>
                <TableHead>Título</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Qtd. ações</TableHead>
                <TableHead className="text-right">Ações rápidas</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell className="font-medium">{item.code}</TableCell>
                  <TableCell>{item.title}</TableCell>
                  <TableCell>{statusBadge(item.is_active)}</TableCell>
                  <TableCell>{item.items_count}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <PermissionIconButton
                        permission={readPermission}
                        onClick={() => navigate(`${basePath}/${item.id}`)}
                        label="Visualizar"
                      >
                        <Eye className="h-4 w-4" />
                      </PermissionIconButton>

                      <PermissionIconButton
                        permission={writePermission}
                        onClick={() => navigate(`${basePath}/${item.id}/editar`)}
                        label="Editar"
                      >
                        <Pencil className="h-4 w-4" />
                      </PermissionIconButton>

                      {item.is_active ? (
                        <PermissionIconButton
                          permission={writePermission}
                          onClick={() => toggleStatus(item.id, false)}
                          label="Inativar"
                        >
                          <XCircle className="h-4 w-4" />
                        </PermissionIconButton>
                      ) : (
                        <PermissionIconButton
                          permission={writePermission}
                          onClick={() => toggleStatus(item.id, true)}
                          label="Ativar"
                        >
                          <CheckCircle2 className="h-4 w-4" />
                        </PermissionIconButton>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
